import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { IAlarmCentralProps } from '../../../services/AlarmCentralService/types';
import { getStreamByEndpointAsync } from '../../../services/Events';
import { ENDPOINT_ALARM_CENTRAL } from '../../../services/AlarmCentralService';
import { AppContext } from 'context/AppContextProvider';
import { AlarmCentralJflProblems } from '../../../services/AlarmCentralService/Physical/Jfl/enums';
import PanicAlarmCentralPopupComponent from '../../../components/PanicAlarmCentralPopupComponent';

interface AlarmCentralPhysicalContextProviderProps {
  children: ReactNode;
}

interface IVirtualAlarmCentralContextData {
  isIsecNetV1: boolean;
  alarmCentral: IAlarmCentralProps;
  problems: IAlarmCentralProblemsProps[];
  setProblems: Dispatch<SetStateAction<IAlarmCentralProblemsProps[]>>;
}

export interface IAlarmCentralProblemsProps {
  description: string;
}
export const AlarmCentralPhysicalContext = createContext(
  {} as IVirtualAlarmCentralContextData,
);

const AlarmCentralPhysicalContextProvider = ({
  children,
}: AlarmCentralPhysicalContextProviderProps) => {
  const { id } = useParams();
  const [alarmCentral, setAlarmCentral] = useState<IAlarmCentralProps>(
    {} as IAlarmCentralProps,
  );
  const [problems, setProblems] = useState<IAlarmCentralProblemsProps[]>([]);
  const { showSnackbar } = useContext(AppContext);
  const [isIsecNetV1, setIsIsecNetV1] = useState<boolean>(false);

  useEffect(() => {
    if (alarmCentral) {
      switch (alarmCentral.model) {
        case 'AMT8000':
        case 'AMT8000PRO':
        case 'AMT8000LITE':
          setIsIsecNetV1(false);
          break;
        default:
          setIsIsecNetV1(true);
      }
    }
  }, [alarmCentral]);

  useEffect(() => {
    const problemsToList: IAlarmCentralProblemsProps[] = [];

    if (
      alarmCentral &&
      alarmCentral.status &&
      alarmCentral.status.problems &&
      alarmCentral.status.problems.length > 0
    ) {
      alarmCentral.status.problems.forEach(
        (problem: AlarmCentralJflProblems) => {
          switch (problem) {
            case AlarmCentralJflProblems.LOW_BATTERY_WIRELESS_SENSOR:
              problemsToList.push({
                description:
                  'Bateria fraca de controle remoto ou sensor sem fio',
              });
              break;

            case AlarmCentralJflProblems.SENSOR_SUPERVISION:
              problemsToList.push({
                description: 'Supervisão do sensor',
              });
              break;

            case AlarmCentralJflProblems.AUXILIARY_OUTPUT:
              problemsToList.push({
                description: 'Problema na saída auxiliar',
              });
              break;

            case AlarmCentralJflProblems.TAMPER:
              problemsToList.push({
                description: 'Violação (Tamper) detectada',
              });
              break;

            case AlarmCentralJflProblems.DHCP:
              problemsToList.push({
                description: 'Problema com DHCP',
              });
              break;

            case AlarmCentralJflProblems.NETWORK_CABLE:
              problemsToList.push({
                description: 'Cabo de rede desconectado',
              });
              break;

            case AlarmCentralJflProblems.CELL_MODULE:
              problemsToList.push({
                description: 'Falha no módulo de celular',
              });
              break;

            case AlarmCentralJflProblems.SMS:
              problemsToList.push({
                description: 'Falha no envio de SMS',
              });
              break;

            case AlarmCentralJflProblems.ETHERNET:
              problemsToList.push({
                description: 'Falha na conexão Ethernet',
              });
              break;

            case AlarmCentralJflProblems.GPRS:
              problemsToList.push({
                description: 'Falha na conexão GPRS',
              });
              break;

            case AlarmCentralJflProblems.PHONE_LINE:
              problemsToList.push({
                description: 'Problema na linha telefônica',
              });
              break;

            case AlarmCentralJflProblems.SHORT_CIRCUIT:
              problemsToList.push({
                description: 'Curto-circuito detectado',
              });
              break;

            case AlarmCentralJflProblems.KEYBOARD:
              problemsToList.push({
                description: 'Falha no teclado',
              });
              break;

            case AlarmCentralJflProblems.SIREN:
              problemsToList.push({
                description: 'Falha na sirene',
              });
              break;

            case AlarmCentralJflProblems.BATTERY:
              problemsToList.push({
                description: 'Bateria da central fraca',
              });
              break;

            case AlarmCentralJflProblems.AC_POWER:
              problemsToList.push({
                description: 'Falta de alimentação AC',
              });
              break;

            case AlarmCentralJflProblems.INVERTED_BATTERY:
              problemsToList.push({
                description: 'Bateria invertida ou em curto',
              });
              break;

            case AlarmCentralJflProblems.DESTINATION_IP_2:
              problemsToList.push({
                description: 'IP de destino 2 inválido',
              });
              break;

            case AlarmCentralJflProblems.DESTINATION_IP_1:
              problemsToList.push({
                description: 'IP de destino 1 inválido',
              });
              break;

            case AlarmCentralJflProblems.DNS_SERVER:
              problemsToList.push({
                description: 'Problema com servidor DNS',
              });
              break;

            case AlarmCentralJflProblems.AC_KEYBOARD_NETWORK:
              problemsToList.push({
                description: 'Rede de teclado AC com problema',
              });
              break;

            case AlarmCentralJflProblems.SIREN_SUPERVISION:
              problemsToList.push({
                description: 'Supervisão da sirene com falha',
              });
              break;

            case AlarmCentralJflProblems.WIFI_PASSWORD:
              problemsToList.push({
                description: 'Senha da rede Wi-Fi incorreta',
              });
              break;

            case AlarmCentralJflProblems.WIFI_AUTH:
              problemsToList.push({
                description: 'Falha na autenticação da rede Wi-Fi',
              });
              break;

            case AlarmCentralJflProblems.SSID_NOT_FOUND:
              problemsToList.push({
                description: 'SSID da rede Wi-Fi não encontrado',
              });
              break;

            case AlarmCentralJflProblems.IP_CONFLICT:
              problemsToList.push({
                description: 'Conflito de IP detectado',
              });
              break;

            case AlarmCentralJflProblems.BUS:
              problemsToList.push({
                description: 'Falha no barramento',
              });
              break;

            case AlarmCentralJflProblems.DDNS:
              problemsToList.push({
                description: 'Problema com DDNS',
              });
              break;

            case AlarmCentralJflProblems.NOTIFICATION:
              problemsToList.push({
                description: 'Erro no sistema de notificação',
              });
              break;

            case AlarmCentralJflProblems.ETHERNET_MODULE:
              problemsToList.push({
                description: 'Falha no módulo Ethernet',
              });
              break;

            case AlarmCentralJflProblems.SIGNAL_LEVEL_OR_OPERATOR:
              problemsToList.push({
                description: 'Problema no nível de sinal ou operadora',
              });
              break;

            case AlarmCentralJflProblems.SIM_CARD:
              problemsToList.push({
                description: 'Problema no chip de celular (SIM card)',
              });
              break;

            case AlarmCentralJflProblems.KEYBOARD_TAMPER:
              problemsToList.push({
                description: 'Violação física do teclado (tamper)',
              });
              break;

            case AlarmCentralJflProblems.PGM_SUPERVISION:
              problemsToList.push({
                description: 'Falha na supervisão da PGM',
              });
              break;
          }
        },
      );
    }
    setProblems(problemsToList);
  }, [alarmCentral]);

  useEffect(() => {
    if (id) {
      getStreamByEndpointAsync(
        ENDPOINT_ALARM_CENTRAL + `/${id}`,
        setAlarmCentral,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        undefined,
        false,
        (message) => showSnackbar(message, 'error'),
      );
    }
  }, []);

  return (
    <AlarmCentralPhysicalContext.Provider
      value={{ alarmCentral, isIsecNetV1, problems, setProblems }}
    >
      {children}
      <PanicAlarmCentralPopupComponent />
    </AlarmCentralPhysicalContext.Provider>
  );
};

export default AlarmCentralPhysicalContextProvider;
